<!-- Calling this orders because we might have an ACTUAL transactions log -->
<template>
  <div class="min-h-full">
    <div
      class="mb-4 flex flex-col items-center justify-between gap-8 py-2 md:flex-row-reverse"
    >
      <div
        class="flex flex-col items-center justify-between gap-2 md:flex-row"
      >
        <ReportCashierFilter v-model="filters.cashierId" />
        <ReportDeviceFilter v-model="filters.deviceId" />
        <DateFilter
          :model-value="globalQuery"
          :time-period-label="timePeriodLabel"
          range-type="custom"
          @update:model-value="setGlobalQuery"
        />
      </div>

      <SearchInput v-model="searchRaw" />
    </div>

    <QueryLoading v-if="isLoading" class="relative h-24" />
    <QueryError
      v-else-if="isError"
      :show-retry="true"
      class="m-auto"
      @retry="refetch"
    >
      Failed to load orders
    </QueryError>

    <template v-else>
      <div>
        <div class="flex w-full justify-center"> </div>
        <template v-if="data">
          <template v-if="data.items.length > 0">
            <div class="mb-3">
              <template
                v-for="[
                  date,
                  entries,
                ] in combinedItemsGroupedByDate"
                :key="date"
              >
                <div class="px-2 py-3 text-sm">
                  {{ date }}
                </div>
                <ol class="-mx-2 mb-6 space-y-1 py-px">
                  <template
                    v-for="entry in entries"
                    :key="
                      entry.kind === 'order'
                        ? `order-${entry.order.id}`
                        : `zreport-${entry.zReport.id}`
                    "
                  >
                    <li v-if="entry.kind === 'order'">
                      <RouterLink
                        :to="{
                          name: 'orderDetail',
                          params: {
                            orderId: entry.order.id,
                          },
                        }"
                      >
                        <OrdersIndexListItem
                          :order="entry.order"
                        />
                      </RouterLink>
                    </li>
                    <li v-else>
                      <RouterLink
                        :to="{
                          name: 'zReportDetail',
                          params: {
                            zReportId: entry.zReport.id,
                          },
                        }"
                      >
                        <ZReportsIndexListItem
                          :report="entry.zReport"
                        />
                      </RouterLink>
                    </li>
                  </template>
                </ol>
              </template>
            </div>
            <div class="mb-4 flex justify-around px-8">
              <BasePagination
                v-model:page="page"
                :enable-next="enableNext"
                :enable-previous="enablePrevious"
              />
            </div>
          </template>

          <template v-else>
            <p
              class="w-full px-8 py-4 text-center text-background-fg/70"
            >
              No transactions to display
            </p>
          </template>
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  TransactionsFeedCursor,
  TransactionsFeedFilters,
  TransactionsFeedItem,
  TransactionsFeedQuery,
} from "@/services/orders/listTransactionsFeed"
import { useTransactionsFeed } from "@/services/orders/listTransactionsFeed"

const filters = reactive({
  deviceId: undefined as number | undefined,
  cashierId: undefined as string | undefined,
})
const searchRaw = ref<string>("")
const { globalQuery, setGlobalQuery, timePeriodLabel } =
  useReportQuery({})
const search = refDebounced(searchRaw, 750)

const feedFilters = computed<TransactionsFeedFilters>(
  () => ({
    ...filters,
    ...globalQuery.value,
    search: search.value?.trim() || undefined,
  })
)

const {
  page,
  enableNext,
  enablePrevious,
  paginationQuery,
  resetPagination,
} = useCursorPagination<TransactionsFeedCursor>({
  initialCursor: {
    cursorAt: undefined,
    cursorPriority: undefined,
  },
  nextCursor: computed(() => ({
    cursorAt: data.value?.nextCursorAt,
    cursorPriority: data.value?.nextCursorPriority,
  })),
  enableNext: computed(() => data.value?.hasNext ?? false),
  limit: 10,
})

watch(
  () => feedFilters.value,
  () => resetPagination(),
  { deep: true, immediate: true }
)

const query = reactiveComputed<TransactionsFeedQuery>(
  () => ({
    ...feedFilters.value,
    ...paginationQuery.value,
  })
)

const { data, isError, refetch, isLoading } =
  useTransactionsFeed(query)

const combinedItemsGroupedByDate = computed(() => {
  if (!data.value)
    return new Map<string, TransactionsFeedItem[]>()

  const groupedMap = new Map<
    string,
    TransactionsFeedItem[]
  >()

  for (const item of data.value.items) {
    const datePart = formatToDate(item.createdAt)
    const existing = groupedMap.get(datePart) || []
    groupedMap.set(datePart, [...existing, item])
  }

  return groupedMap
})
</script>
